// mission-resolver.ts
//
// Resolves WHICH mission `run`/`status`/control commands operate on, from
// `--mission <id>` / `--repo <path>` / ambient cwd. Multiple missions with no
// disambiguator => structured error, never a guess (tests 16-17, spec §3.3:
// "Nếu repo có nhiều mission chưa chọn được, trả lỗi rõ; không đoán mission
// gần nhất.").
//
// Layout convention (spec §3.2): `<workspace_root>/.avatar/missions/<id>/`
// is mission_home; `<mission_home>/runtime` is control root.

import { dirname, join, resolve } from "node:path";
import { pathExists } from "../filesystem-helpers.js";

// D13: mission id charset — blocks `/`, `..`, and empty string so
// `--mission` can never escape `.avatar/missions/<id>` (path traversal would
// let `writeDirective`/`unlock` write or delete outside the mission dir).
const VALID_MISSION_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

/** D13: true when `id` is safe to join into a mission path — checked BEFORE any disk access. */
export function isValidMissionId(id: string): boolean {
  return VALID_MISSION_ID_PATTERN.test(id);
}

export interface ResolvedMission {
  missionId: string;
  missionHome: string;
  controlRoot: string;
}

export class MissionResolutionError extends Error {
  readonly candidates: string[];
  constructor(message: string, candidates: string[] = []) {
    super(message);
    this.name = "MissionResolutionError";
    this.candidates = candidates;
  }
}

function missionsDir(workspaceRoot: string): string {
  return join(workspaceRoot, ".avatar", "missions");
}

export function missionHomePath(workspaceRoot: string, missionId: string): string {
  return join(missionsDir(workspaceRoot), missionId);
}

export function controlRootPath(workspaceRoot: string, missionId: string): string {
  return join(missionHomePath(workspaceRoot, missionId), "runtime");
}

/**
 * The inverse of the layout above, from a mission workspace
 * (`<ws>/.avatar/missions/<id>/workspace`): its control root and the workspace
 * root. Re-audit 06g N7 — the deny bindings the settings re-apply needs when
 * only the mission workspace is at hand.
 */
export function missionLayoutFromWorkspace(missionWorkspace: string): {
  controlRoot: string;
  workspaceRoot: string;
} {
  return {
    controlRoot: join(dirname(missionWorkspace), "runtime"),
    workspaceRoot: resolve(missionWorkspace, "..", "..", "..", ".."),
  };
}

async function listMissionIds(workspaceRoot: string): Promise<string[]> {
  const dir = missionsDir(workspaceRoot);
  if (!(await pathExists(dir))) return [];
  const { promises: fs } = await import("node:fs");
  const entries = await fs.readdir(dir, { withFileTypes: true });
  return entries.filter((e) => e.isDirectory()).map((e) => e.name);
}

export interface ResolveMissionInput {
  workspaceRoot: string;
  explicitMissionId?: string;
}

/**
 * Resolve exactly one mission. Explicit `--mission <id>` always wins (test 17)
 * as long as it exists. Without it: zero missions => error (nothing to run);
 * exactly one => resolved unambiguously; two-or-more => structured error
 * listing candidates, NEVER guessing the most-recent (test 16).
 */
export async function resolveMission(input: ResolveMissionInput): Promise<ResolvedMission> {
  if (input.explicitMissionId !== undefined) {
    if (!isValidMissionId(input.explicitMissionId)) {
      throw new MissionResolutionError(
        `Mission id không hợp lệ: "${input.explicitMissionId}". ` +
          "Chỉ chấp nhận chữ/số/-/_ (không '/', không '..', không rỗng).",
      );
    }
    const home = missionHomePath(input.workspaceRoot, input.explicitMissionId);
    if (!(await pathExists(home))) {
      throw new MissionResolutionError(
        `Mission "${input.explicitMissionId}" không tồn tại tại ${home}.`,
      );
    }
    return {
      missionId: input.explicitMissionId,
      missionHome: home,
      controlRoot: controlRootPath(input.workspaceRoot, input.explicitMissionId),
    };
  }

  const ids = await listMissionIds(input.workspaceRoot);
  if (ids.length === 0) {
    throw new MissionResolutionError(
      `Không có mission nào trong ${missionsDir(input.workspaceRoot)}. Chạy 'avatar autopilot init' trước.`,
    );
  }
  if (ids.length > 1) {
    throw new MissionResolutionError(
      `Có ${ids.length} mission trong repo, chưa chọn được: ${ids.join(", ")}. ` +
        "Dùng '--mission <id>' để chỉ rõ — không tự đoán mission gần nhất.",
      ids,
    );
  }

  const missionId = ids[0] as string;
  return {
    missionId,
    missionHome: missionHomePath(input.workspaceRoot, missionId),
    controlRoot: controlRootPath(input.workspaceRoot, missionId),
  };
}
